import type React from 'react';
import dayjs from 'dayjs';
import {
  Controller,
  useFormContext,
  type FieldErrors,
  type RegisterOptions,
} from 'react-hook-form';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { NULL_VALUE } from 'constants/displayConstants';
import { TiptapRichTextEditor } from 'components/DesignSystems/New/TiptapRichTextEditor';
import { ICS204_LABEL, ICS204_TIPTAP } from './assignmentCardStyles';
import { isIcs204RichTextEmpty } from './ics204RichText';

/**
 * Shadcn-backed replacements for the shared `components/IAP/formFields/*`
 * inputs, scoped to the ICS-204 card.
 *
 * These bind through `Controller` rather than `register` because the shadcn
 * primitives are plain function components: on React 18 a `ref` arriving via
 * `{...register()}` is dropped with a warning, leaving the field untracked.
 */

/** Walks a dotted path so nested names like `resource.<id>.leader` resolve. */
const resolveError = (
  errors: FieldErrors,
  path: string
): string | undefined => {
  const node = path
    .split('.')
    .reduce<unknown>(
      (acc, key) =>
        acc && typeof acc === 'object'
          ? (acc as Record<string, unknown>)[key]
          : undefined,
      errors
    );

  const message = (node as { message?: unknown } | undefined)?.message;
  return typeof message === 'string' ? message : undefined;
};

/** ISO string to the `YYYY-MM-DDTHH:mm` a `datetime-local` input expects. */
const toDateTimeLocal = (value: string | undefined): string => {
  if (!value) return '';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DDTHH:mm') : '';
};

const fromDateTimeLocal = (value: string): string => {
  if (!value) return '';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.toISOString() : '';
};

type FieldShellProps = {
  label?: React.ReactNode;
  htmlFor?: string;
  error?: string;
  className?: string;
};

/** Shadcn Field shell with ICS-204 panel-readable labels. */
const FieldShell: React.FC<React.PropsWithChildren<FieldShellProps>> = ({
  label,
  htmlFor,
  error,
  className,
  children,
}) => (
  <Field
    data-invalid={error ? true : undefined}
    className={cn('tw:gap-1', className)}
  >
    {label ? (
      <FieldLabel htmlFor={htmlFor} className={ICS204_LABEL}>
        {label}
      </FieldLabel>
    ) : null}
    {children}
    {error ? <FieldError className="tw:text-xs">{error}</FieldError> : null}
  </Field>
);

type ReadOnlyFieldProps = {
  label: string;
  value?: string | null;
  /** Kept alongside `data-testid` so the ICS-204 box stays self-describing. */
  title?: string;
  testId?: string;
  className?: string;
};

/** Label-above-value pair used throughout the read-only card body. */
export const Ics204ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({
  label,
  value,
  title,
  testId,
  className,
}) => {
  const display = value?.trim() ? value : NULL_VALUE;
  return (
    <Field className={cn('tw:min-w-0 tw:gap-1', className)} title={title}>
      <FieldLabel className={ICS204_LABEL}>{label}</FieldLabel>
      <span
        className="tw:block tw:min-w-0 tw:break-words tw:break-all tw:text-foreground"
        data-testid={testId}
      >
        {display}
      </span>
    </Field>
  );
};

type FormFieldProps = {
  label?: React.ReactNode;
  fieldName: string;
  testId: string;
  rules?: RegisterOptions;
  disabled?: boolean;
  className?: string;
};

export const Ics204TextField: React.FC<FormFieldProps> = ({
  label,
  fieldName,
  testId,
  rules,
  disabled = false,
  className,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const id = `${fieldName}-input`;

  return (
    <FieldShell
      label={label}
      htmlFor={id}
      error={resolveError(errors, fieldName)}
      className={className}
    >
      <Controller
        control={control}
        name={fieldName}
        rules={rules}
        render={({ field }) => (
          <Input
            id={id}
            data-testid={testId}
            disabled={disabled}
            name={field.name}
            value={(field.value as string | undefined) ?? ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
          />
        )}
      />
    </FieldShell>
  );
};

type NumberFieldProps = FormFieldProps & {
  min?: number;
  step?: number;
};

export const Ics204NumberField: React.FC<NumberFieldProps> = ({
  label,
  fieldName,
  testId,
  rules,
  disabled = false,
  className,
  min,
  step,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const id = `${fieldName}-input`;

  return (
    <FieldShell
      label={label}
      htmlFor={id}
      error={resolveError(errors, fieldName)}
      className={className}
    >
      <Controller
        control={control}
        name={fieldName}
        rules={rules}
        render={({ field }) => (
          <Input
            id={id}
            type="number"
            data-testid={testId}
            disabled={disabled}
            min={min}
            step={step}
            name={field.name}
            value={(field.value as number | string | undefined) ?? ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
          />
        )}
      />
    </FieldShell>
  );
};

/**
 * `datetime-local` bound to an ISO string. The control speaks
 * `YYYY-MM-DDTHH:mm` in the viewer's local zone, so values are converted on the
 * way in and back out to keep the form value serializable.
 */
export const Ics204DateTimeField: React.FC<FormFieldProps> = ({
  label,
  fieldName,
  testId,
  rules,
  disabled = false,
  className,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const id = `${fieldName}-datetime`;

  return (
    <FieldShell
      label={label}
      htmlFor={id}
      error={resolveError(errors, fieldName)}
      className={className}
    >
      <Controller
        control={control}
        name={fieldName}
        rules={rules}
        render={({ field }) => (
          <Input
            id={id}
            type="datetime-local"
            data-testid={testId}
            disabled={disabled}
            name={field.name}
            value={toDateTimeLocal(field.value as string | undefined)}
            onChange={(event) =>
              field.onChange(fromDateTimeLocal(event.target.value))
            }
            onBlur={field.onBlur}
          />
        )}
      />
    </FieldShell>
  );
};

export const Ics204TextAreaField: React.FC<FormFieldProps> = ({
  label,
  fieldName,
  testId,
  rules,
  disabled = false,
  className,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const id = `${fieldName}-textarea`;

  return (
    <FieldShell
      label={label}
      htmlFor={id}
      error={resolveError(errors, fieldName)}
      className={className}
    >
      <Controller
        control={control}
        name={fieldName}
        rules={rules}
        render={({ field }) => (
          <Textarea
            id={id}
            data-testid={testId}
            disabled={disabled}
            name={field.name}
            value={(field.value as string | undefined) ?? ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
          />
        )}
      />
    </FieldShell>
  );
};

type RichTextFieldProps = FormFieldProps & {
  placeholder?: string;
  minHeight?: string;
};

/** TipTap editor bound through RHF (report-section parity, no mentions/images). */
export const Ics204RichTextField: React.FC<RichTextFieldProps> = ({
  label,
  fieldName,
  testId,
  rules,
  disabled = false,
  className,
  placeholder,
  minHeight = '120px',
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <FieldShell
      label={label}
      error={resolveError(errors, fieldName)}
      className={className}
    >
      <div data-testid={testId}>
        <Controller
          control={control}
          name={fieldName}
          rules={rules}
          render={({ field }) => (
            <TiptapRichTextEditor
              value={(field.value as string | undefined) ?? ''}
              onChange={field.onChange}
              placeholder={placeholder}
              disabled={disabled}
              readOnly={disabled}
              showToolbar={!disabled}
              minHeight={minHeight}
              className={ICS204_TIPTAP}
            />
          )}
        />
      </div>
    </FieldShell>
  );
};

type RichTextViewProps = {
  value?: string | null;
  testId?: string;
  className?: string;
  emptyLabel?: string;
};

/** Read-only TipTap for view mode (preserves newlines / basic styling). */
export const Ics204RichTextView: React.FC<RichTextViewProps> = ({
  value,
  testId,
  className,
  emptyLabel = NULL_VALUE,
}) => {
  if (isIcs204RichTextEmpty(value)) {
    return (
      <span
        className={cn('tw:text-[var(--text-weak)]', className)}
        data-testid={testId}
      >
        {emptyLabel}
      </span>
    );
  }

  return (
    <div data-testid={testId} className={className}>
      <TiptapRichTextEditor
        value={value ?? ''}
        readOnly
        showToolbar={false}
        minHeight="2rem"
      />
    </div>
  );
};

export const Ics204CheckboxField: React.FC<FormFieldProps> = ({
  label,
  fieldName,
  testId,
  disabled = false,
  className,
}) => {
  const { control } = useFormContext();
  const id = `${fieldName}-checkbox`;

  return (
    <div className={cn('tw:flex tw:items-center tw:gap-2', className)}>
      <Controller
        control={control}
        name={fieldName}
        render={({ field }) => (
          <Checkbox
            id={id}
            data-testid={testId}
            disabled={disabled}
            name={field.name}
            checked={!!field.value}
            onCheckedChange={(checked) => field.onChange(checked)}
          />
        )}
      />
      {label ? (
        <Label htmlFor={id} className={ICS204_LABEL}>
          {label}
        </Label>
      ) : null}
    </div>
  );
};

export type Ics204SelectOption = {
  value: string;
  label: string;
  /** Rendered but unselectable, e.g. a work group another card already claims. */
  disabled?: boolean;
};

type SelectFieldProps = FormFieldProps & {
  options: Ics204SelectOption[];
  /** Label for a leading empty-value option; omit to require a real choice. */
  placeholder?: string;
  /**
   * Called before the form value updates. Return `false` to reject the change
   * (e.g. open a modal for a sentinel option instead of persisting it).
   */
  onSelectChange?: (next: string, current: string) => boolean | void;
};

/** Shadcn Select bound through Controller for ICS-204 edit dropdowns. */
export const Ics204SelectField: React.FC<SelectFieldProps> = ({
  label,
  fieldName,
  testId,
  rules,
  disabled = false,
  className,
  options,
  placeholder,
  onSelectChange,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const id = `${fieldName}-select`;
  const error = resolveError(errors, fieldName);

  return (
    <FieldShell label={label} htmlFor={id} error={error} className={className}>
      <Controller
        control={control}
        name={fieldName}
        rules={rules}
        render={({ field }) => {
          const current = (field.value as string | undefined) ?? '';
          const selectItems = [
            ...(placeholder !== undefined
              ? [{ value: null as string | null, label: placeholder }]
              : []),
            ...options.map((option) => ({
              value: option.value as string | null,
              label: option.label,
            })),
          ];
          return (
            <Select
              items={selectItems}
              value={current === '' ? null : current}
              onValueChange={(next) => {
                const nextValue = next ?? '';
                if (onSelectChange?.(nextValue, current) === false) return;
                field.onChange(nextValue);
              }}
              disabled={disabled}
              name={field.name}
            >
              <SelectTrigger
                id={id}
                data-testid={testId}
                aria-invalid={error ? true : undefined}
                className="tw:w-full"
              >
                <SelectValue placeholder={placeholder ?? 'Select…'} />
              </SelectTrigger>
              <SelectContent
                alignItemWithTrigger={false}
                className="dark tw:bg-black tw:text-foreground"
              >
                {placeholder !== undefined ? (
                  <SelectItem value={null}>{placeholder}</SelectItem>
                ) : null}
                {options.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        }}
      />
    </FieldShell>
  );
};
